import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Link,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Debt, Expense, ExpenseSection } from "../lib/types";
import { debtAPI, expenseAPI } from "../services/api/api";
import { comparisonItemsAPI, ComparisonItemData } from "../services/api/api";

type ExpenseMatcher = {
  name: string;
  vendor?: string;
};

type ComparisonTab = "expenses" | "debts";

type DebtSpreadsheetItem = {
  label: string;
  monthlyPayment: number;
  status: string;
};

// Default seed data – used only when the user has no saved items yet
const DEFAULT_ITEMS: ComparisonItemData[] = [
  {
    label: "1Password for Family",
    price: 6.99,
    matchers: [{ name: "Vault for Family", vendor: "1Password" }],
  },
  {
    label: "1Password for TPC",
    price: 4.99,
    matchers: [{ name: "Vault", vendor: "1Password" }],
  },
  {
    label: "Auto Insurance - Geico",
    price: 343.72,
    matchers: [{ name: "Insurance - Auto", vendor: "Geico" }],
  },
  { label: "Chicken Feed", price: 16.0, matchers: [] },
  { label: "Dog Food", price: 45.0, matchers: [{ name: "Dog Food" }] },
  {
    label: "Apple One (Fitness, Photo Storage, Music, TV)",
    price: 37.99,
    matchers: [{ name: "Apple One", vendor: "Apple" }],
  },
  { label: "Date Nights", price: 0.0, matchers: [{ name: "Date Nights" }] },
  {
    label: "Debt - Central Oregon Emergency Physicians",
    price: 25.0,
    matchers: [],
  },
  {
    label: "Debt - Credit Cards - Amazon (American Express) TPC's - 62002",
    price: 35.0,
    matchers: [],
  },
  {
    label: "Debt - RV - Oregon State Credit Union",
    price: 347.0,
    matchers: [{ name: "RV", vendor: "Oregon State Credit Union" }],
  },
  {
    label: "Debt - Credit Cards - Amazon (Synchrony) Brittanys - 4561",
    price: 93.0,
    matchers: [],
  },
  {
    label: "Debt - Credit Cards - Amazon (Synchrony) Spencers - 5974",
    price: 88.0,
    matchers: [],
  },
  {
    label: "Debt - Credit Cards - Apple",
    price: 150.0,
    matchers: [{ name: "Debt - Credit Cards - Apple", vendor: "Apple" }],
  },
  { label: "Debt - Credit Cards - Chase", price: 40.0, matchers: [] },
  {
    label: "Debt - Credit Cards - CreditOne 9076 (Brittany's)",
    price: 50.0,
    matchers: [],
  },
  {
    label: "Debt - Credit Cards - Hilton (American Express)",
    price: 70.0,
    matchers: [],
  },
  {
    label: "Debt - Credit Cards - Best Buy - Citi - 9981",
    price: 78.0,
    matchers: [],
  },
  {
    label: "Debt - Credit Cards - Kay Jewelers (Comenity)",
    price: 32.0,
    matchers: [],
  },
  { label: "Debt - Mortgage", price: 227.43, matchers: [{ name: "Mortgage" }] },
  { label: "Debt - PayPal Working Capital", price: 1049.26, matchers: [] },
  { label: "Debt - Stripe Working Capital", price: 1791.0, matchers: [] },
  {
    label: "Debt - Taxes - Federal",
    price: 284.0,
    matchers: [{ name: "Taxes - 2023 Federal", vendor: "USA" }],
  },
  {
    label: "Debt - Taxes - State",
    price: 300.0,
    matchers: [{ name: "Taxes - 2023 State", vendor: "State of Oregon" }],
  },
  {
    label: "Debt - Tesla Model X",
    price: 382.62,
    matchers: [{ name: "Auto Loan - Tesla Model X" }],
  },
  {
    label: "Debt - Toyota Financial - Sienna",
    price: 392.5,
    matchers: [{ name: "Sienna", vendor: "Toyota" }],
  },
  {
    label: "School, Diapers, Wipes, Chemicals",
    price: 125.0,
    matchers: [
      { name: "Diapers, Wipes, Chemicals" },
      { name: "Daipers, Wipes, Cleaning Supplies" },
    ],
  },
  {
    label: "Disney+ & Hulu",
    price: 19.99,
    matchers: [{ name: "Disney+ & Hulu" }],
  },
  { label: "Electricity", price: 250.0, matchers: [{ name: "Electricity" }] },
  { label: "Fuel", price: 260.0, matchers: [{ name: "Fuel" }] },
  {
    label: "Google Apps for TPC",
    price: 16.99,
    matchers: [{ name: "Email & Drive", vendor: "Google" }],
  },
  {
    label: "Google Drive - Personal",
    price: 9.99,
    matchers: [{ name: "Email & Drive", vendor: "Google" }],
  },
  { label: "Groceries", price: 1250.0, matchers: [{ name: "Groceries" }] },
  {
    label: "Insurance - Renters - Lemonade",
    price: 7.89,
    matchers: [{ name: "Insurance - Renters", vendor: "Lemonade" }],
  },
  {
    label: "Insurance - Life - Ladder",
    price: 35.71,
    matchers: [{ name: "Insurance - Life", vendor: "Ladder" }],
  },
  {
    label: "Entertainment - Netflix",
    price: 19.99,
    matchers: [{ name: "Netflix" }],
  },
  {
    label: "Email - Proton",
    price: 4.99,
    matchers: [{ name: "Email", vendor: "Proton" }],
  },
  { label: "Hosting - Railway Pro", price: 25.0, matchers: [] },
  { label: "Rent", price: 2609.0, matchers: [] },
  {
    label: "Skylight - Family Digital Organizer",
    price: 7.99,
    matchers: [{ name: "Digital Family Organizer", vendor: "Skylight" }],
  },
  {
    label: "Internet - Starlink",
    price: 200.0,
    matchers: [{ name: "Internet", vendor: "Starlink" }],
  },
  {
    label: "Phone - T-Mobile",
    price: 207.59,
    matchers: [{ name: "Phones, Tablets, Watches", vendor: "T-Mobile" }],
  },
];

// Source: exports/Budget & Forecast - Debt.csv
const DEBT_SPREADSHEET_ITEMS: DebtSpreadsheetItem[] = [
  { label: "Affirm - Server", monthlyPayment: 32.55, status: "Inactive" },
  {
    label: "Amazon Business (American Express) 62002",
    monthlyPayment: 150.0,
    status: "Cancelled",
  },
  {
    label: "Amazon Synchrony (Brittany's) 4561",
    monthlyPayment: 100.0,
    status: "Active",
  },
  {
    label: "Amazon Synchrony (Spencer's) 5974",
    monthlyPayment: 100.0,
    status: "Active",
  },
  { label: "Apple Card", monthlyPayment: 200.0, status: "Active" },
  {
    label: "Best Buy 9981 - Citi Bank",
    monthlyPayment: 150.0,
    status: "Active",
  },
  { label: "Chase", monthlyPayment: 50.0, status: "Current" },
  { label: "Best Buy Citi", monthlyPayment: 0.0, status: "Closed 5/8/23" },
  {
    label: "Central Oregon Emergency Physicians",
    monthlyPayment: 25.0,
    status: "Active",
  },
  {
    label: "CreditOne 0681 - Spencer's",
    monthlyPayment: 35.0,
    status: "Active",
  },
  {
    label: "CreditOne 9076 - Brittany's",
    monthlyPayment: 35.0,
    status: "Active",
  },
  { label: "Deborah Erickson", monthlyPayment: 0.0, status: "Paid" },
  { label: "Deborah for Dental", monthlyPayment: 0.0, status: "Paid" },
  { label: "Federal Taxes", monthlyPayment: 0.0, status: "Paid" },
  { label: "Federal Taxes - 2023", monthlyPayment: 284.0, status: "Active" },
  {
    label: "Hilton - American Express - 32011",
    monthlyPayment: 47.98,
    status: "Active",
  },
  { label: "Karell", monthlyPayment: 0.0, status: "N/A" },
  { label: "Leland", monthlyPayment: 0.0, status: "N/A" },
  {
    label: "Kay Jewelers (Commenity)",
    monthlyPayment: 100.0,
    status: "Active",
  },
  {
    label: "Medical - LaPine Community Health Center (Arlos SSN)",
    monthlyPayment: 0.0,
    status: "Paid",
  },
  {
    label: "Medical - St. Charles SCMC.org",
    monthlyPayment: 50.0,
    status: "Paid",
  },
  { label: "Mortgage - 1st Payment", monthlyPayment: 227.43, status: "Active" },
  {
    label: "PayPal hello@brittanyhillstudios.com",
    monthlyPayment: 0.0,
    status: "Active",
  },
  { label: "PayPal Working Capital", monthlyPayment: 3600.0, status: "Active" },
  { label: "Rodney Hill for Gift", monthlyPayment: 0.0, status: "Active" },
  { label: "Rodney Hill for Instapot", monthlyPayment: 0.0, status: "Active" },
  {
    label: "Rodney Hill for Therapy for Brittany",
    monthlyPayment: 0.0,
    status: "Active",
  },
  { label: "Rodney Hill for Unknown", monthlyPayment: 0.0, status: "Active" },
  {
    label: "Rodney Hill for Water Heater",
    monthlyPayment: 0.0,
    status: "Active",
  },
  {
    label: "Rodney Hill for December Mortgage",
    monthlyPayment: 0.0,
    status: "N/A",
  },
  {
    label: "Rodney Hill for Personal Loan (Bills)",
    monthlyPayment: 0.0,
    status: "N/A",
  },
  {
    label: "Rodney Hill for Rent November",
    monthlyPayment: 0.0,
    status: "N/A",
  },
  {
    label: "Rodney Hill for Rent December",
    monthlyPayment: 0.0,
    status: "N/A",
  },
  { label: "RV", monthlyPayment: 347.0, status: "Active" },
  { label: "State of Oregon Taxes", monthlyPayment: 0.0, status: "Paid" },
  {
    label: "State of Oregon Taxes - 2023",
    monthlyPayment: 300.0,
    status: "Active",
  },
  { label: "Stripe Working Capital", monthlyPayment: 3600.0, status: "Active" },
  { label: "T-Mobile - Brittany's Phone", monthlyPayment: 0.0, status: "Paid" },
  {
    label: "T-Mobile - Brittany's Watch",
    monthlyPayment: 0.0,
    status: "Active",
  },
  { label: "T-Mobile - Owen's Watch", monthlyPayment: 0.0, status: "Active" },
  { label: "T-Mobile - Spencer's Phone", monthlyPayment: 0.0, status: "Paid" },
  { label: "Taylor Hill for Rent", monthlyPayment: 0.0, status: "N/A" },
  {
    label: "Taylor Hill for Electricity Bill",
    monthlyPayment: 0.0,
    status: "N/A",
  },
  {
    label: "Taylor Hill for General Loan",
    monthlyPayment: 0.0,
    status: "Active",
  },
  {
    label: "Taylor Hill for Gift for Water Heater & Hoses",
    monthlyPayment: 0.0,
    status: "Active",
  },
  {
    label: "Taylor Hill for Speeding Ticket",
    monthlyPayment: 0.0,
    status: "N/A",
  },
  {
    label: "Taylor Hill for Tax Preperation",
    monthlyPayment: 100.0,
    status: "Active",
  },
  {
    label: "Toyota Seqioua (Owed to Dad)",
    monthlyPayment: 339.01,
    status: "Active",
  },
  { label: "Toyota Sienna", monthlyPayment: 391.45, status: "Active" },
];

const normalize = (value?: string) => (value ?? "").trim().toLowerCase();
const normalizeDebtKey = (value?: string) =>
  normalize(value).replace(/[^a-z0-9]/g, "");

const matchesExpense = (expense: Expense, matcher: ExpenseMatcher) => {
  if (normalize(expense.name) !== normalize(matcher.name)) return false;
  if (!matcher.vendor) return true;
  return normalize(expense.vendor) === normalize(matcher.vendor);
};

const matchesDebt = (debt: Debt, label: string) => {
  const target = normalizeDebtKey(label);
  if (!target) return false;

  const candidates = [
    debt.account,
    debt.creditor,
    [debt.creditor, debt.account].filter(Boolean).join(" - "),
    [debt.creditor, debt.account].filter(Boolean).join(" "),
  ];

  return candidates.some((candidate) => normalizeDebtKey(candidate) === target);
};

const dedupeComparisonItems = (items: ComparisonItemData[]) => {
  const seen = new Set<string>();
  const deduped: ComparisonItemData[] = [];
  const duplicates: string[] = [];

  items.forEach((item) => {
    const key = normalize(item.label);
    if (seen.has(key)) {
      duplicates.push(item.label);
      return;
    }
    seen.add(key);
    deduped.push(item);
  });

  if (duplicates.length) {
    console.warn("[ExpensesComparison] Duplicate comparison item labels", {
      duplicates,
    });
  }

  return { deduped, duplicates };
};

const getSectionByDueDate = (date: Date, now: Date): ExpenseSection => {
  const todayStart = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  );
  const targetDate = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  const diffDays = Math.floor(
    (targetDate.getTime() - todayStart.getTime()) / 86400000
  );

  if (diffDays < 0) return "overdue";
  if (diffDays === 0) return "today";
  if (diffDays === 1) return "tomorrow";
  if (diffDays <= 7) return "thisWeek";
  if (diffDays <= 10) return "next10Days";

  const isSameMonth =
    targetDate.getUTCFullYear() === todayStart.getUTCFullYear() &&
    targetDate.getUTCMonth() === todayStart.getUTCMonth();
  if (isSameMonth) return "thisMonth";

  const nextMonth = new Date(
    Date.UTC(todayStart.getUTCFullYear(), todayStart.getUTCMonth() + 1, 1)
  );
  const isNextMonth =
    targetDate.getUTCFullYear() === nextMonth.getUTCFullYear() &&
    targetDate.getUTCMonth() === nextMonth.getUTCMonth();
  if (isNextMonth) return "nextMonth";

  return "expected";
};

const formatCurrency = (value: number) =>
  value.toLocaleString("en-US", { style: "currency", currency: "USD" });

const ExpensesComparisonPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ComparisonTab>("expenses");
  const [comparisonItems, setComparisonItems] = useState<ComparisonItemData[]>(
    []
  );
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Add-to-expenses dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addLabel, setAddLabel] = useState("");
  const [addAmount, setAddAmount] = useState("");
  const [addDueDate, setAddDueDate] = useState("");
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addingDebtLabel, setAddingDebtLabel] = useState<string | null>(null);

  const handleOpenAddDialog = (label: string, price: number) => {
    console.debug("[ExpensesComparison] Opening add dialog for:", label, {
      price,
    });
    setAddLabel(label);
    setAddAmount(price > 0 ? price.toString() : "");
    setAddDueDate("");
    setAddError(null);
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setAddLabel("");
    setAddAmount("");
    setAddDueDate("");
    setAddError(null);
  };

  const handleAddExpense = async () => {
    if (!addAmount || !addDueDate) {
      setAddError("Amount and Due Date are required.");
      return;
    }
    const parsedAmount = parseFloat(addAmount);
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      setAddError("Amount must be a positive number.");
      return;
    }

    setAddSaving(true);
    setAddError(null);

    const newExpense: Omit<Expense, "id"> = {
      name: addLabel,
      amount: parsedAmount,
      dueDate: new Date(addDueDate),
      isPaid: false,
      isRecurring: true,
      recurrencePattern: "monthly",
      vendor: "",
      internalCategory: "",
      billOrBudget: "bill",
    };

    console.debug("[ExpensesComparison] Creating expense:", newExpense);

    try {
      const created = await expenseAPI.add(newExpense);
      console.log("[ExpensesComparison] Expense created:", created);
      const refreshed = await expenseAPI.getAll();
      setExpenses(refreshed);
      handleCloseAddDialog();
    } catch (err) {
      console.error("[ExpensesComparison] Failed to create expense:", err);
      setAddError("Failed to create expense. Please try again.");
    } finally {
      setAddSaving(false);
    }
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    console.log("[ExpensesComparison] Loading data...");
    try {
      const [expensesData, itemsData, debtsData] = await Promise.all([
        expenseAPI.getAll(),
        comparisonItemsAPI.getAll(),
        debtAPI.getAll(),
      ]);
      console.log("[ExpensesComparison] Loaded", {
        expenses: expensesData.length,
        comparisonItems: itemsData.length,
        debts: debtsData.length,
      });

      setExpenses(expensesData);
      setDebts(debtsData);

      const { deduped, duplicates } = dedupeComparisonItems(itemsData);

      // Seed default items if the user has none yet
      if (deduped.length === 0) {
        console.log("[ExpensesComparison] No saved items, seeding defaults...");
        const seeded = await comparisonItemsAPI.saveAll(DEFAULT_ITEMS);
        const seededResult = dedupeComparisonItems(seeded);
        console.log(
          "[ExpensesComparison] Seeded",
          seededResult.deduped.length,
          "items"
        );
        setComparisonItems(seededResult.deduped);
        if (seededResult.duplicates.length) {
          await comparisonItemsAPI.saveAll(seededResult.deduped);
          console.log("[ExpensesComparison] Removed duplicate seeded items");
        }
      } else {
        setComparisonItems(deduped);
        if (duplicates.length) {
          await comparisonItemsAPI.saveAll(deduped);
          console.log("[ExpensesComparison] Removed duplicate items");
        }
      }
    } catch (err) {
      console.error("[ExpensesComparison] Failed to load data", err);
      setError("Failed to load data.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const rows = useMemo(() => {
    const now = new Date();
    const sixMonthsAgo = new Date(now);
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

    return comparisonItems.map((item, index) => {
      const matchedExpenses = item.matchers.length
        ? expenses.filter((expense) =>
            item.matchers.some((matcher) => matchesExpense(expense, matcher))
          )
        : [];
      const appears = matchedExpenses.length > 0;
      const recurring = matchedExpenses.some((expense) => expense.isRecurring);
      const sixMonths = matchedExpenses.some((expense) => {
        if (!expense.createdAt) return false;
        return expense.createdAt <= sixMonthsAgo;
      });
      const expenseLink =
        matchedExpenses.length > 0
          ? (() => {
              const first = matchedExpenses[0];
              const dueDate =
                first.dueDate instanceof Date
                  ? first.dueDate
                  : new Date(first.dueDate);
              const section = getSectionByDueDate(dueDate, now);
              return `/expenses?section=${section}&highlight=${first.id}`;
            })()
          : null;

      return {
        key: `${item.id ?? item.label}-${index}`,
        label: item.label,
        price: item.price,
        appears,
        recurring,
        sixMonths,
        matchedCount: matchedExpenses.length,
        expenseLink,
      };
    });
  }, [expenses, comparisonItems]);

  const debtRows = useMemo(
    () =>
      DEBT_SPREADSHEET_ITEMS.map((item, index) => {
        const matchedDebt = debts.find((debt) => matchesDebt(debt, item.label));
        const appears = Boolean(matchedDebt);
        const debtLink = matchedDebt
          ? `/debts?editDebtId=${encodeURIComponent(matchedDebt.id)}`
          : null;

        return {
          key: `${item.label}-${index}`,
          label: item.label,
          monthlyPayment: item.monthlyPayment,
          spreadsheetStatus: item.status,
          appears,
          debtId: matchedDebt?.id ?? null,
          debtLink,
          dbBalance:
            matchedDebt && Number.isFinite(Number(matchedDebt.balance))
              ? Number(matchedDebt.balance)
              : null,
          dbCreditLimit:
            matchedDebt && Number.isFinite(Number(matchedDebt.creditLimit))
              ? Number(matchedDebt.creditLimit)
              : null,
          dbStatus: matchedDebt?.status ?? "",
        };
      }),
    [debts]
  );

  const totalPrice = useMemo(
    () => comparisonItems.reduce((sum, item) => sum + item.price, 0),
    [comparisonItems]
  );
  const columnTotals = useMemo(
    () => ({
      appears: rows
        .filter((row) => row.appears)
        .reduce((sum, row) => sum + row.price, 0),
      recurring: rows
        .filter((row) => row.recurring)
        .reduce((sum, row) => sum + row.price, 0),
    }),
    [rows]
  );
  const debtTotals = useMemo(
    () => ({
      scheduled: debtRows.reduce((sum, row) => sum + row.monthlyPayment, 0),
      onDebtsPage: debtRows
        .filter((row) => row.appears)
        .reduce((sum, row) => sum + row.monthlyPayment, 0),
    }),
    [debtRows]
  );

  const handleAddDebt = useCallback(
    async (row: {
      label: string;
      monthlyPayment: number;
      spreadsheetStatus: string;
    }) => {
      setAddingDebtLabel(row.label);
      try {
        const payload: Omit<Debt, "id"> = {
          account: row.label,
          type: "Other",
          balance: row.monthlyPayment,
          payoffStrategy: "",
          creditAffecting: "",
          creditLimit: 0,
          paydownAmount: 0,
          maxMonthlyPayment: row.monthlyPayment,
          dueDate: "",
          status: row.spreadsheetStatus || "Active",
          statementCredits: 0,
        };

        await debtAPI.add(payload);
        const refreshedDebts = await debtAPI.getAll();
        setDebts(refreshedDebts);
      } catch (err) {
        console.error("[ExpensesComparison] Failed to create debt:", err);
      } finally {
        setAddingDebtLabel(null);
      }
    },
    []
  );

  useEffect(() => {
    console.log("[ExpensesComparison] Comparison rows", {
      total: rows.length,
      matched: rows.filter((row) => row.appears).length,
      totalPrice,
    });
  }, [rows, totalPrice]);

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Expenses Comparison
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Comparison against the current Expenses and Debts data.
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, next: ComparisonTab) => setActiveTab(next)}
        sx={{ mb: 2 }}
      >
        <Tab value="expenses" label="Expenses" />
        <Tab value="debts" label="Debts" />
      </Tabs>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <>
          {activeTab === "expenses" ? (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                    >
                      Expense
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="right"
                    >
                      Price
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="center"
                    >
                      On Expenses Page
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="center"
                    >
                      Recurring
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="center"
                    >
                      6+ Months in Data DB
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="center"
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow
                      key={row.key}
                      hover
                      sx={(theme) => ({
                        "& > *": { py: 1.25 },
                        bgcolor:
                          Number(row.key.split("-").pop() || 0) % 2 === 0
                            ? theme.palette.background.paper
                            : theme.palette.action.hover,
                      })}
                    >
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.price)}
                      </TableCell>
                      <TableCell align="center">
                        {row.appears && row.expenseLink ? (
                          <Link
                            component={RouterLink}
                            to={row.expenseLink}
                            underline="hover"
                            sx={{ fontWeight: 700, color: "inherit" }}
                          >
                            X
                          </Link>
                        ) : (
                          <Box
                            component="span"
                            sx={{ display: "inline-block", width: 12 }}
                          >
                            &nbsp;
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {row.recurring && row.expenseLink ? (
                          <Link
                            component={RouterLink}
                            to={row.expenseLink}
                            underline="hover"
                            sx={{ fontWeight: 700, color: "inherit" }}
                          >
                            X
                          </Link>
                        ) : (
                          <Box
                            component="span"
                            sx={{ display: "inline-block", width: 12 }}
                          >
                            &nbsp;
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {row.sixMonths ? "X" : ""}
                      </TableCell>
                      <TableCell align="center">
                        {!row.appears && (
                          <Tooltip title="Add to Expenses" arrow>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenAddDialog(row.label, row.price)
                              }
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.appears && (
                          <Box
                            component="span"
                            sx={{ display: "inline-block", width: 12 }}
                          >
                            &nbsp;
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total row */}
                  <TableRow
                    sx={{
                      "& td": {
                        fontWeight: 700,
                        borderTop: 2,
                        borderColor: "divider",
                      },
                    }}
                  >
                    <TableCell>Total</TableCell>
                    <TableCell align="right">
                      {formatCurrency(totalPrice)}
                    </TableCell>
                    <TableCell align="center">
                      {formatCurrency(columnTotals.appears)}
                    </TableCell>
                    <TableCell align="center">
                      {formatCurrency(columnTotals.recurring)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                    >
                      Debt (Spreadsheet)
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="right"
                    >
                      Monthly Payment
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="center"
                    >
                      Spreadsheet Status
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="right"
                    >
                      Balance
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="right"
                    >
                      Credit Limit
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="center"
                    >
                      On Debts Page
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="center"
                    >
                      DB Status
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 700, bgcolor: "action.hover" }}
                      align="center"
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {debtRows.map((row, index) => (
                    <TableRow
                      key={row.key}
                      hover
                      sx={(theme) => ({
                        "& > *": { py: 1.25 },
                        bgcolor:
                          index % 2 === 0
                            ? theme.palette.background.paper
                            : theme.palette.action.hover,
                      })}
                    >
                      <TableCell>{row.label}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(row.monthlyPayment)}
                      </TableCell>
                      <TableCell align="center">
                        {row.spreadsheetStatus}
                      </TableCell>
                      <TableCell align="right">
                        {row.dbBalance == null
                          ? ""
                          : formatCurrency(row.dbBalance)}
                      </TableCell>
                      <TableCell align="right">
                        {row.dbCreditLimit == null
                          ? ""
                          : formatCurrency(row.dbCreditLimit)}
                      </TableCell>
                      <TableCell align="center">
                        {row.appears && row.debtLink ? (
                          <Link
                            component={RouterLink}
                            to={row.debtLink}
                            underline="hover"
                            sx={{ fontWeight: 700, color: "inherit" }}
                          >
                            X
                          </Link>
                        ) : (
                          <Box
                            component="span"
                            sx={{ display: "inline-block", width: 12 }}
                          >
                            &nbsp;
                          </Box>
                        )}
                      </TableCell>
                      <TableCell align="center">{row.dbStatus}</TableCell>
                      <TableCell align="center">
                        {!row.appears && (
                          <Tooltip title="Add to Debts" arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleAddDebt(row)}
                                disabled={addingDebtLabel === row.label}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {row.appears && (
                          <Box
                            component="span"
                            sx={{ display: "inline-block", width: 12 }}
                          >
                            &nbsp;
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow
                    sx={{
                      "& td": {
                        fontWeight: 700,
                        borderTop: 2,
                        borderColor: "divider",
                      },
                    }}
                  >
                    <TableCell>Total</TableCell>
                    <TableCell align="right">
                      {formatCurrency(debtTotals.scheduled)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell align="center">
                      {formatCurrency(debtTotals.onDebtsPage)}
                    </TableCell>
                    <TableCell />
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Add to Expenses Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add to Expenses</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Expense Name"
              value={addLabel}
              InputProps={{ readOnly: true }}
              fullWidth
              size="small"
            />
            <TextField
              label="Amount"
              type="number"
              inputProps={{ min: 0, step: 0.01 }}
              value={addAmount}
              onChange={(e) => setAddAmount(e.target.value)}
              fullWidth
              size="small"
              autoFocus
            />
            <TextField
              label="Due Date"
              type="date"
              value={addDueDate}
              onChange={(e) => setAddDueDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Type"
              value="Recurring (Monthly)"
              InputProps={{ readOnly: true }}
              fullWidth
              size="small"
            />
            {addError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {addError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={addSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleAddExpense}
            variant="contained"
            disabled={addSaving}
          >
            {addSaving ? "Saving..." : "Add Expense"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExpensesComparisonPage;
